import { useMemo } from 'react';
import useStateMachine from '@cassiozen/usestatemachine';

const makeQuickResponse = (statusCode, msg) => (res) => {
  res.status(statusCode).json({ message: msg });
};

export const notAllowed = makeQuickResponse(405, 'Method not allowed');
export const forbidden = makeQuickResponse(403, 'Access forbidden');
export const notFound = makeQuickResponse(404, 'Not found');

/**
 * Utility to only allow certain HTTP verbs and restrict access to
 * the API.
 */
export const prepareRoute = (
  handler,
  { methods = ['GET'], restrictAccess = true } = {},
) => {
  return (req, res) => {
    const { headers } = req;

    if (!methods.includes(req.method)) return notAllowed(res);
    if (
      restrictAccess &&
      (!headers['x-api-key'] || headers['x-api-key'] !== process.env.API_SECRET)
    )
      return forbidden(res);
    return handler(req, res);
  };
};

export const apiRoute = (path) => {
  return `/api${path}`;
};

const API_CACHE = new Map();

export const useApiData = (path, method = 'GET', headers = {}) => {
  const apiUrl = apiRoute(path);

  const [dataState] = useStateMachine({
    initial: 'loading',
    context: { data: {} },
    states: {
      loading: {
        on: { FAIL: 'failed', LOADED: 'loaded' },
        effect: async ({ send, setContext }) => {
          try {
            let data;
            if (!API_CACHE.has(apiUrl)) {
              const res = await fetch(apiUrl, {
                method,
                headers,
              });
              data = await res.json();

              API_CACHE.set(apiUrl, data);
            } else {
              data = API_CACHE.get(apiUrl);
            }

            setContext((c) => ({
              ...c,
              data,
            }));

            send('LOADED');
          } catch {
            send('FAIL');
          }
        },
      },
      loaded: {},
      failed: {},
    },
  });

  const isLoading = useMemo(() => dataState.value === 'loading', [dataState]);
  const hasLoaded = useMemo(() => dataState.value === 'loaded', [dataState]);
  const hasFailed = useMemo(() => dataState.value === 'failed', [dataState]);
  const data = useMemo(() => dataState.context.data, [dataState]);

  return { isLoading, hasLoaded, hasFailed, data };
};
